// Drives a PaymentIntent the backend created (and may already have attempted)
// to completion in the browser. Shared by the checkout form and the add-on
// quantity modal so a 3-D Secure challenge behaves the same on both.
//
// A subscription created with payment_behavior "default_incomplete" returns its
// intent unconfirmed (`requires_confirmation`, or `requires_payment_method`
// while the PaymentMethod minted on the page is not on it yet), and nothing
// charges the card until it is confirmed here. A saved-card charge comes back
// already confirmed: succeeded, or `requires_action` for its 3DS challenge.
//
// Resolves { ok: true } once the intent is paid or processing;
// { ok: false, error } with Stripe's message when Stripe reported one;
// { ok: false, reason } when the intent ended in a state that is not paid.
export async function settlePaymentIntent(stripe, { clientSecret, status, paymentMethodId = null, returnUrl = null }) {
  let current = status

  if (current === "requires_confirmation" || (current === "requires_payment_method" && paymentMethodId)) {
    const { error, paymentIntent } = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        ...(returnUrl ? { return_url: returnUrl } : {}),
        ...(paymentMethodId ? { payment_method: paymentMethodId } : {})
      },
      redirect: "if_required"
    })
    if (error) return { ok: false, error: error.message }
    current = paymentIntent?.status
    if (current === "succeeded" || current === "processing") return { ok: true }
    if (current !== "requires_action") return { ok: false, reason: `not_completed:${current}` }
  }

  if (current === "requires_action") {
    const { error } = await stripe.handleNextAction({ clientSecret })
    return error ? { ok: false, error: error.message } : { ok: true }
  }

  if (current === "requires_payment_method" || current === "canceled") {
    return { ok: false, reason: `failed:${current}` }
  }

  return { ok: true }
}

// Stripe.js has dictionaries for sk/cs/ro/de/fr but not uk
// (docs.stripe.com/js/appendix/supported_locales); "auto" lets it pick from the
// browser instead of receiving a code it does not know.
const STRIPE_LOCALE_OVERRIDES = { uk: "auto" }

export function stripeLocale(lang = document.documentElement.lang) {
  if (!lang) return "auto"

  return STRIPE_LOCALE_OVERRIDES[lang] ?? lang
}
